import React, { useMemo } from 'react';
import { Box, HStack, Skeleton, Text, VStack } from '@chakra-ui/react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RcTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CompanyTimeSeriesResponse,
  TIME_SERIES_CURRENT_ONLY,
  TIME_SERIES_METRICS,
  TimeSeriesGranularity,
  TimeSeriesMetric,
} from '../../../store/responseTypes/company';

const TABULAR = { fontVariantNumeric: 'tabular-nums' as const };

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const formatNumber = (n: number) => n.toLocaleString('en-US');

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log10(bytes) / 3));
  const v = bytes / Math.pow(1000, i);
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
};

const formatBucketLabel = (
  iso: string,
  granularity: TimeSeriesGranularity,
): string => {
  const d = new Date(iso);
  const day = d.getUTCDate();
  const month = MONTHS_SHORT[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  if (granularity === 'month') return `${month} ${year}`;
  return `${month} ${day}`;
};

const METRIC_LABELS: Record<TimeSeriesMetric, string> = {
  signedAgreements: 'Signed agreements',
  registeredAgreements: 'Registered agreements',
  projects: 'Projects',
  userSessions: 'Sessions',
  filesUploaded: 'Files uploaded',
  storageUsedBytes: 'Storage used',
  adminUsers: 'Admin users',
  employeeUsers: 'Employee users',
  storageCapacityMegabytes: 'Storage capacity',
};

// Distinct strokes per metric so multi-line plots remain readable on a
// neutral gray palette without leaning on hue.
const METRIC_STROKES: Record<TimeSeriesMetric, string> = {
  signedAgreements: '#111827',
  registeredAgreements: '#6b7280',
  projects: '#1f2937',
  userSessions: '#4b5563',
  filesUploaded: '#374151',
  storageUsedBytes: '#9ca3af',
  adminUsers: '#6b7280',
  employeeUsers: '#9ca3af',
  storageCapacityMegabytes: '#d1d5db',
};

const isBytesMetric = (m: TimeSeriesMetric): boolean =>
  m === 'storageUsedBytes' || m === 'storageCapacityMegabytes';

const formatMetricValue = (m: TimeSeriesMetric, v: number): string => {
  if (isBytesMetric(m)) return formatBytes(v);
  return formatNumber(v);
};

interface CompanyTimeseriesChartProps {
  timeseries?: CompanyTimeSeriesResponse;
  activeMetrics: TimeSeriesMetric[];
  onToggleMetric: (metric: TimeSeriesMetric) => void;
  isLoading?: boolean;
  isError?: boolean;
}

// Storage capacity comes from BE in MB but storageUsedBytes is bytes;
// normalize capacity to bytes so both can share the chart's Y axis.
const normalizeMetricValue = (m: TimeSeriesMetric, v: number): number =>
  m === 'storageCapacityMegabytes' ? v * 1000 * 1000 : v;

const CompanyTimeseriesChart: React.FC<CompanyTimeseriesChartProps> = ({
  timeseries,
  activeMetrics,
  onToggleMetric,
  isLoading,
  isError,
}) => {
  const data = useMemo(() => {
    if (!timeseries) return [];
    return timeseries.buckets.map((b, i) => {
      const row: Record<string, string | number> = {
        label: formatBucketLabel(b.bucketStart, timeseries.granularity),
      };
      for (const m of TIME_SERIES_METRICS) {
        if (TIME_SERIES_CURRENT_ONLY.includes(m)) {
          const cv = timeseries.currentValues as Record<string, number>;
          row[m] = normalizeMetricValue(m, cv[m] ?? 0);
        } else {
          row[m] = normalizeMetricValue(m, timeseries.series[m]?.[i] ?? 0);
        }
      }
      return row;
    });
  }, [timeseries]);

  return (
    <VStack align="stretch" spacing={5}>
      <HStack spacing={5} flexWrap="wrap">
        {TIME_SERIES_METRICS.map((m) => {
          const active = activeMetrics.includes(m);
          return (
            <Box
              key={m}
              as="button"
              type="button"
              onClick={() => onToggleMetric(m)}
              fontSize="sm"
              color={active ? 'gray.900' : 'gray.400'}
              borderBottom="1px solid"
              borderColor={active ? 'gray.900' : 'transparent'}
              pb={1}
              transition="color 0.1s, border-color 0.1s"
              _hover={{ color: active ? 'gray.900' : 'gray.700' }}
            >
              {METRIC_LABELS[m]}
            </Box>
          );
        })}
      </HStack>

      {isLoading && !timeseries ? (
        <Skeleton h="320px" />
      ) : isError && !timeseries ? (
        <Box h="320px" display="flex" alignItems="center" justifyContent="center">
          <Text fontSize="sm" color="gray.500">
            Couldn't load timeseries.
          </Text>
        </Box>
      ) : !timeseries || timeseries.buckets.length === 0 ? (
        <Box h="320px" display="flex" alignItems="center" justifyContent="center">
          <Text fontSize="sm" color="gray.400">
            No timeseries data for this period.
          </Text>
        </Box>
      ) : (
        <Box h="320px">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#f3f4f6" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                width={48}
                tickFormatter={(v: number) =>
                  activeMetrics.some(isBytesMetric)
                    ? formatBytes(v)
                    : formatNumber(v)
                }
              />
              <RcTooltip
                cursor={{ stroke: '#d1d5db', strokeDasharray: '3 3' }}
                contentStyle={{
                  fontSize: 12,
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  ...TABULAR,
                }}
                formatter={(v: number, name: string) => [
                  formatMetricValue(name as TimeSeriesMetric, v),
                  METRIC_LABELS[name as TimeSeriesMetric] ?? name,
                ]}
              />
              {activeMetrics.map((m) => (
                <Line
                  key={m}
                  type="monotone"
                  dataKey={m}
                  name={m}
                  stroke={METRIC_STROKES[m]}
                  strokeWidth={2}
                  strokeDasharray={
                    TIME_SERIES_CURRENT_ONLY.includes(m) ? '4 4' : undefined
                  }
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </VStack>
  );
};

export default CompanyTimeseriesChart;
